import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const createColoredIcon = (color, label) =>
  L.divIcon({
    className: "aktav-marker",
    html: `<div style="background:${color};width:34px;height:34px;border-radius:50%;border:3px solid #fff;box-shadow:0 4px 10px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;font-size:13px;">${label}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });

const startIcon = L.divIcon({
  className: "aktav-marker-start",
  html: `<div style="background:#0f172a;width:40px;height:40px;border-radius:50%;border:4px solid #fff;box-shadow:0 4px 12px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m3 11 18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg></div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

const pickIcon = L.divIcon({
  className: "aktav-marker-pick",
  html: `<div style="background:#f59e0b;width:38px;height:38px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 6px 16px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;"><svg style="transform:rotate(45deg);" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg></div>`,
  iconSize: [38, 38],
  iconAnchor: [19, 38],
});

const DeliveryMap = ({ orders, route, highlightId, pickMode, pickedPosition, onLocationPick }) => {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markersRef = useRef([]);
  const polylineRef = useRef(null);
  const pickMarkerRef = useRef(null);
  const clickHandlerRef = useRef(null);

  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: [40.9436, 30.5372],
      zoom: 9,
      scrollWheelZoom: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap",
      maxZoom: 19,
    }).addTo(map);

    L.marker([40.9436, 30.5372], { icon: startIcon })
      .bindPopup("<b>Ferizli, Sakarya</b><br/>Başlangıç Noktası")
      .addTo(map);

    mapRef.current = map;
  }, []);

  // Cursor style + click handler for pick mode
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const container = map.getContainer();
    if (pickMode) {
      container.style.cursor = "crosshair";
      if (!clickHandlerRef.current) {
        clickHandlerRef.current = (e) => {
          onLocationPick?.({ lat: e.latlng.lat, lng: e.latlng.lng });
        };
        map.on("click", clickHandlerRef.current);
      }
    } else {
      container.style.cursor = "";
      if (clickHandlerRef.current) {
        map.off("click", clickHandlerRef.current);
        clickHandlerRef.current = null;
      }
    }
    return () => {
      if (clickHandlerRef.current) {
        map.off("click", clickHandlerRef.current);
        clickHandlerRef.current = null;
      }
    };
  }, [pickMode, onLocationPick]);

  // Draggable pick marker
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (pickMarkerRef.current) {
      map.removeLayer(pickMarkerRef.current);
      pickMarkerRef.current = null;
    }
    if (pickedPosition && pickedPosition.lat != null && pickedPosition.lng != null) {
      const m = L.marker([pickedPosition.lat, pickedPosition.lng], {
        icon: pickIcon,
        draggable: true,
      })
        .bindPopup("<b>Seçilen Konum</b><br/>Sürükleyerek düzeltebilirsiniz.")
        .addTo(map);
      m.on("dragend", (e) => {
        const pos = e.target.getLatLng();
        onLocationPick?.({ lat: pos.lat, lng: pos.lng });
      });
      pickMarkerRef.current = m;
      // Auto-open popup once
      setTimeout(() => m.openPopup(), 300);
      map.setView([pickedPosition.lat, pickedPosition.lng], Math.max(map.getZoom(), 12));
    }
  }, [pickedPosition, onLocationPick]);

  // Order markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach((m) => map.removeLayer(m));
    markersRef.current = [];

    const bounds = [[40.9436, 30.5372]];
    orders.forEach((o, idx) => {
      if (o.lat == null || o.lng == null) return;
      const isDelivered = o.status === "delivered";
      const color = isDelivered ? "#059669" : "#b91c1c";
      const label = String(idx + 1);
      const marker = L.marker([o.lat, o.lng], { icon: createColoredIcon(color, label) })
        .bindPopup(
          `<div style="min-width:200px;font-family:Inter,sans-serif;">
             <div style="font-weight:700;color:#0f172a;font-size:14px;margin-bottom:4px;">${o.customer_name}</div>
             <div style="font-size:12px;color:#475569;margin-bottom:6px;">${o.address}</div>
             <div style="display:flex;gap:8px;font-size:11px;">
               <span style="background:${color};color:#fff;padding:2px 8px;border-radius:4px;font-weight:600;">${o.quantity} adet</span>
               <span style="background:#e2e8f0;color:#0f172a;padding:2px 8px;border-radius:4px;">${isDelivered ? "Teslim Edildi" : "Bekliyor"}</span>
             </div>
             ${o.phone ? `<div style="margin-top:6px;font-size:12px;"><a href="tel:${o.phone}" style="color:#b91c1c;">${o.phone}</a></div>` : ""}
             <a href="https://www.google.com/maps/dir/?api=1&destination=${o.lat},${o.lng}" target="_blank" rel="noopener noreferrer" style="display:block;margin-top:8px;background:#4285F4;color:#fff;text-align:center;padding:6px 8px;border-radius:6px;font-size:12px;font-weight:600;text-decoration:none;">Google Maps'te Aç</a>
           </div>`
        )
        .addTo(map);
      markersRef.current.push(marker);
      bounds.push([o.lat, o.lng]);
    });

    if (bounds.length > 1 && !pickedPosition) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [orders, pickedPosition]);

  // Route polyline
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (polylineRef.current) {
      map.removeLayer(polylineRef.current);
      polylineRef.current = null;
    }
    if (route && route.ordered_stops && route.ordered_stops.length) {
      const coords = [[route.start.lat, route.start.lng], ...route.ordered_stops.map((s) => [s.lat, s.lng])];
      polylineRef.current = L.polyline(coords, {
        color: "#b91c1c",
        weight: 4,
        opacity: 0.75,
        dashArray: "8, 8",
      }).addTo(map);
      map.fitBounds(coords, { padding: [50, 50] });
    }
  }, [route]);

  // Highlight
  useEffect(() => {
    if (!highlightId || !mapRef.current) return;
    const map = mapRef.current;
    const idx = orders.findIndex((o) => o.id === highlightId);
    if (idx === -1) return;
    const o = orders[idx];
    if (o.lat == null) return;
    map.setView([o.lat, o.lng], 13, { animate: true });
    const m = markersRef.current[idx];
    if (m) m.openPopup();
  }, [highlightId, orders]);

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} className="w-full h-full rounded-lg overflow-hidden" />
      {pickMode && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] bg-amber-500 text-white px-4 py-2 rounded-full shadow-lg text-sm font-semibold pointer-events-none animate-pulse">
          📍 Haritada bir noktaya tıklayın
        </div>
      )}
    </div>
  );
};

export default DeliveryMap;
